import { jsPDF } from 'jspdf';

// All sizes are PDF points at 100% zoom.
const CARD_W = 210;
const MIN_CARD_H = 66;
const H_GAP = 20;
const V_GAP = 44;
const ROW_GAP = 10;
const STACK_INDENT = 18;
const STACK_MAX_ROWS = 10;
const MARGIN = 36;
const HEADER_H = 74;
const FOOTER_H = 24;
const LEGEND_ROW_H = 16;
const MIN_PAGE_W = 842;
const MIN_PAGE_H = 595;
const MAX_PAGE_UNITS = 14400;
const AVATAR_R = 17;
const ACCENT_W = 4;
const TEXT_X = ACCENT_W + 12 + AVATAR_R * 2 + 10;
const TEXT_W = CARD_W - TEXT_X - 10;
const MAX_LINES = 2;
const TEXT_STYLES = {
  name: { style: 'bold', size: 9.5, lineH: 11.5 },
  designation: { style: 'normal', size: 8.5, lineH: 10.5 },
  department: { style: 'normal', size: 8.5, lineH: 10.5 },
};

// Card height is shared by every card so levels stay aligned; it grows to fit
// the longest wrapped text (capped at MAX_LINES per field).
let CARD_H = MIN_CARD_H;

const COLORS = {
  line: [148, 163, 184],
  border: [226, 232, 240],
  shadow: [226, 232, 240],
  card: [255, 255, 255],
  name: [15, 23, 42],
  muted: [71, 85, 105],
  faint: [148, 163, 184],
  pill: [241, 245, 249],
  pillText: [71, 85, 105],
  company: [23, 37, 84],
  companyAccent: [99, 102, 241],
  companyText: [255, 255, 255],
  companySubtext: [199, 210, 254],
  eyebrow: [79, 70, 229],
  hod: [217, 119, 6],
  hodText: [255, 255, 255],
};

// Department accents: [accent, light tint]. Amber is left out so it only ever means HOD.
const DEPARTMENT_PALETTE = [
  [[37, 99, 235], [239, 246, 255]], // blue
  [[13, 148, 136], [240, 253, 250]], // teal
  [[225, 29, 72], [255, 241, 242]], // rose
  [[124, 58, 237], [245, 243, 255]], // violet
  [[101, 163, 13], [247, 254, 231]], // lime
  [[192, 38, 211], [253, 244, 255]], // fuchsia
  [[154, 52, 18], [255, 247, 237]], // brown
  [[8, 145, 178], [236, 254, 255]], // cyan
  [[71, 85, 105], [241, 245, 249]], // slate
  [[22, 101, 52], [240, 253, 244]], // forest
];
const NO_DEPARTMENT = [[100, 116, 139], [241, 245, 249]];

const darken = (rgb, factor = 0.78) => rgb.map((value) => Math.round(value * factor));

// Colours are assigned in alphabetical order so each department in the chart
// gets a distinct one (until the palette wraps).
const assignDepartmentColors = (root) => {
  const names = new Set();
  const collect = (node) => {
    if (!node.is_company && node.department) {
      names.add(node.department);
    }
    (node.children || []).forEach(collect);
  };
  collect(root);

  const colors = new Map();
  [...names].sort((a, b) => a.localeCompare(b)).forEach((name, index) => {
    colors.set(name, DEPARTMENT_PALETTE[index % DEPARTMENT_PALETTE.length]);
  });
  return colors;
};

const isLeaf = (node) => !node.children?.length;

// A manager's direct reports who have no team of their own are drawn as a
// block of vertical columns instead of a single row, which keeps wide, flat
// teams from stretching the page sideways. Reports with teams stay side by side.
const layoutItems = (node) => {
  const leaves = node.children.filter(isLeaf);
  if (leaves.length < 2) {
    return node.children.map((child) => ({ node: child }));
  }

  const cols = Math.ceil(leaves.length / STACK_MAX_ROWS);
  const stack = {
    members: leaves,
    cols,
    rows: Math.ceil(leaves.length / cols),
    w: cols * (STACK_INDENT + CARD_W) + (cols - 1) * H_GAP,
  };
  return [...node.children.filter((child) => !isLeaf(child)).map((child) => ({ node: child })), { stack }];
};

const itemWidth = (item) => (item.stack ? item.stack.w : item.node.w);

const measure = (node) => {
  if (isLeaf(node)) {
    node.w = CARD_W;
    return node.w;
  }

  node.items = layoutItems(node);
  const itemsW = node.items.reduce((sum, item) => sum + (item.stack ? item.stack.w : measure(item.node)), 0) + H_GAP * (node.items.length - 1);
  node.w = Math.max(CARD_W, itemsW);
  return node.w;
};

// Assigns card positions (x, y = top-left) and returns the lowest y used.
const place = (node, left, top) => {
  node.x = left + (node.w - CARD_W) / 2;
  node.y = top;
  let bottom = top + CARD_H;

  if (isLeaf(node)) {
    return bottom;
  }

  const childTop = top + CARD_H + V_GAP;
  const itemsW = node.items.reduce((sum, item) => sum + itemWidth(item), 0) + H_GAP * (node.items.length - 1);
  let cursor = left + (node.w - itemsW) / 2;

  node.items.forEach((item) => {
    if (item.stack) {
      const { stack } = item;
      stack.members.forEach((child, index) => {
        const col = Math.floor(index / stack.rows);
        const row = index % stack.rows;
        child.x = cursor + col * (STACK_INDENT + CARD_W + H_GAP) + STACK_INDENT;
        child.y = childTop + row * (CARD_H + ROW_GAP);
        child.w = CARD_W;
        bottom = Math.max(bottom, child.y + CARD_H);
      });
    } else {
      bottom = Math.max(bottom, place(item.node, cursor, childTop));
    }
    cursor += itemWidth(item) + H_GAP;
  });
  return bottom;
};

const initialsOf = (name) => (name || '?')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part[0].toUpperCase())
  .join('') || '?';

const imageFormatOf = (dataUri) => (/^data:image\/png/i.test(dataUri) ? 'PNG' : 'JPEG');

const pad2 = (value) => String(value).padStart(2, '0');

export const organogramFileName = (companyName, date = new Date()) => {
  const safeName = (companyName || 'Company').replace(/[\\/:*?"<>|]+/g, '').trim().replace(/\s+/g, '_') || 'Company';
  return `Organogram_${safeName}_${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}.pdf`;
};

/**
 * Builds a single-page vector PDF of the full reporting tree returned by
 * `companies/getOrganogramExport`. The page grows to fit the chart; past the
 * 14400-unit PDF limit the page's UserUnit is raised so it still prints at
 * full size instead of being clipped.
 */
const wrapLines = (doc, text, maxW) => {
  const lines = doc.splitTextToSize(text, maxW);
  if (lines.length <= MAX_LINES) {
    return lines;
  }

  let last = lines[MAX_LINES - 1];
  while (last.length > 1 && doc.getTextWidth(`${last}...`) > maxW) {
    last = last.slice(0, -1);
  }
  return [...lines.slice(0, MAX_LINES - 1), `${last.trimEnd()}...`];
};

const prepareText = (doc, node) => {
  if (!node.is_company) {
    const values = {
      name: node.code ? `${node.name || ''} (${node.code})` : node.name || '',
      designation: node.designation || '-',
      department: node.department || '-',
    };
    node.lines = {};
    let textH = 0;
    Object.entries(TEXT_STYLES).forEach(([key, style]) => {
      doc.setFont('helvetica', style.style);
      doc.setFontSize(style.size);
      node.lines[key] = wrapLines(doc, values[key], TEXT_W);
      textH += node.lines[key].length * style.lineH;
    });
    CARD_H = Math.max(CARD_H, Math.ceil(textH + 24));
  }
  (node.children || []).forEach((child) => prepareText(doc, child));
};

export const buildOrganogramPdf = ({ companyName, root, exportedAt = new Date() }) => {
  CARD_H = MIN_CARD_H;
  const measureDoc = new jsPDF({ unit: 'pt' });
  prepareText(measureDoc, root);
  measure(root);
  const chartBottom = place(root, 0, 0);

  const departmentColors = assignDepartmentColors(root);
  const colorsOf = (node) => departmentColors.get(node.department) || NO_DEPARTMENT;
  let employeeCount = 0;
  const countEmployees = (node) => {
    employeeCount += node.is_company ? 0 : 1;
    (node.children || []).forEach(countEmployees);
  };
  countEmployees(root);

  const pageW = Math.max(MIN_PAGE_W, root.w + MARGIN * 2);

  // Legend chips wrap across the page width; measured before the page exists.
  measureDoc.setFont('helvetica', 'normal');
  measureDoc.setFontSize(8);
  const legend = [];
  let legendX = 0;
  let legendRow = 0;
  departmentColors.forEach((colors, name) => {
    const chipW = 14 + measureDoc.getTextWidth(name) + 16;
    if (legendX > 0 && legendX + chipW > pageW - MARGIN * 2) {
      legendX = 0;
      legendRow += 1;
    }
    legend.push({ name, colors, x: legendX, row: legendRow });
    legendX += chipW;
  });
  const legendH = legend.length ? (legendRow + 1) * LEGEND_ROW_H + 10 : 0;
  const headerH = HEADER_H + legendH;

  const pageH = Math.max(MIN_PAGE_H, MARGIN * 2 + headerH + chartBottom + FOOTER_H);
  const unit = Math.max(1, Math.ceil(Math.max(pageW, pageH) / MAX_PAGE_UNITS));
  const s = (value) => value / unit;

  const doc = new jsPDF({
    orientation: pageW >= pageH ? 'landscape' : 'portrait',
    unit: 'pt',
    format: [s(pageW), s(pageH)],
    userUnit: unit,
    compress: true,
  });
  doc.setProperties({ title: `${companyName} - Organogram`, subject: 'Organogram', creator: 'Klok HRMS' });

  const ox = (pageW - root.w) / 2;
  const oy = MARGIN + headerH;

  const fitText = (text, maxW) => {
    let value = text || '';
    if (doc.getTextWidth(value) <= s(maxW)) {
      return value;
    }
    while (value.length > 1 && doc.getTextWidth(`${value}...`) > s(maxW)) {
      value = value.slice(0, -1);
    }
    return `${value.trimEnd()}...`;
  };

  const pill = (cx, cy, label, fill, textColor, border) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(s(7));
    const w = doc.getTextWidth(label) * unit + 14;
    doc.setFillColor(...fill);
    if (border) {
      doc.setDrawColor(...border);
      doc.setLineWidth(s(0.6));
    }
    doc.roundedRect(s(cx - w / 2), s(cy - 6.5), s(w), s(13), s(6.5), s(6.5), border ? 'FD' : 'F');
    doc.setTextColor(...textColor);
    doc.text(label, s(cx), s(cy), { align: 'center', baseline: 'middle' });
    return w;
  };

  // Header: thin brand strip, eyebrow, company name, export details, legend.
  doc.setFillColor(...COLORS.company);
  doc.rect(0, 0, s(pageW), s(5), 'F');
  doc.setFillColor(...COLORS.companyAccent);
  doc.rect(0, s(5), s(pageW), s(1.5), 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(s(8.5));
  doc.setTextColor(...COLORS.eyebrow);
  doc.text('ORGANOGRAM', s(MARGIN), s(MARGIN + 6), { charSpace: s(1.5) });
  doc.setFontSize(s(22));
  doc.setTextColor(...COLORS.name);
  doc.text(fitText(companyName, pageW - MARGIN * 2 - 280), s(MARGIN), s(MARGIN + 32));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(s(9.5));
  doc.setTextColor(...COLORS.muted);
  doc.text('Reporting structure across the organization', s(MARGIN), s(MARGIN + 48));

  const exportedLabel = `Exported on ${exportedAt.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })}, ${exportedAt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`;
  const summary = `${employeeCount} ${employeeCount === 1 ? 'employee' : 'employees'}  |  ${departmentColors.size} ${departmentColors.size === 1 ? 'department' : 'departments'}`;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(s(10));
  doc.setTextColor(...COLORS.name);
  doc.text(summary, s(pageW - MARGIN), s(MARGIN + 18), { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(s(9));
  doc.setTextColor(...COLORS.muted);
  doc.text(exportedLabel, s(pageW - MARGIN), s(MARGIN + 32), { align: 'right' });

  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(s(0.75));
  doc.line(s(MARGIN), s(MARGIN + 60), s(pageW - MARGIN), s(MARGIN + 60));

  doc.setFontSize(s(8));
  legend.forEach(({ name, colors, x, row }) => {
    const cy = MARGIN + 72 + row * LEGEND_ROW_H;
    doc.setFillColor(...colors[0]);
    doc.roundedRect(s(MARGIN + x), s(cy - 4), s(9), s(9), s(2), s(2), 'F');
    doc.setTextColor(...COLORS.muted);
    doc.text(name, s(MARGIN + x + 14), s(cy + 0.5), { baseline: 'middle' });
  });

  // Footer
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(s(7.5));
  doc.setTextColor(...COLORS.faint);
  doc.text('Generated by Klok HRMS', s(MARGIN), s(pageH - MARGIN / 2 - 4));
  doc.text('Confidential - for internal use only', s(pageW - MARGIN), s(pageH - MARGIN / 2 - 4), { align: 'right' });

  // Connectors first, so cards sit on top of them.
  doc.setDrawColor(...COLORS.line);
  doc.setLineWidth(s(1));
  const line = (x1, y1, x2, y2) => doc.line(s(ox + x1), s(oy + y1), s(ox + x2), s(oy + y2));

  const drawConnectors = (node) => {
    if (isLeaf(node)) {
      return;
    }

    const cx = node.x + CARD_W / 2;
    const busY = node.y + CARD_H + V_GAP / 2;
    line(cx, node.y + CARD_H, cx, busY);

    const drops = [];
    node.items.forEach((item) => {
      if (!item.stack) {
        const childCx = item.node.x + CARD_W / 2;
        drops.push(childCx);
        line(childCx, busY, childCx, item.node.y);
        drawConnectors(item.node);
        return;
      }

      const { stack } = item;
      Array.from({ length: stack.cols }, (_, col) => stack.members.slice(col * stack.rows, (col + 1) * stack.rows)).forEach((members) => {
        const spineX = members[0].x - STACK_INDENT / 2;
        const lastMid = members[members.length - 1].y + CARD_H / 2;
        drops.push(spineX);
        line(spineX, busY, spineX, lastMid);
        members.forEach((child) => line(spineX, child.y + CARD_H / 2, child.x, child.y + CARD_H / 2));
      });
    });
    line(Math.min(cx, ...drops), busY, Math.max(cx, ...drops), busY);
  };

  const drawAvatar = (node, cx, cy, [accent, tint]) => {
    doc.setDrawColor(...accent);
    doc.setLineWidth(s(1.25));

    if (node.photo) {
      try {
        doc.saveGraphicsState();
        doc.circle(s(cx), s(cy), s(AVATAR_R), null);
        doc.clip();
        doc.discardPath();
        doc.addImage(node.photo, imageFormatOf(node.photo), s(cx - AVATAR_R), s(cy - AVATAR_R), s(AVATAR_R * 2), s(AVATAR_R * 2), `photo-${node.id}`, 'FAST');
        doc.restoreGraphicsState();
        doc.circle(s(cx), s(cy), s(AVATAR_R), 'S');
        return;
      } catch (e) {
        // Unreadable image data: fall through to the initials avatar.
        doc.restoreGraphicsState();
      }
    }

    doc.setFillColor(...tint);
    doc.circle(s(cx), s(cy), s(AVATAR_R), 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(s(11));
    doc.setTextColor(...darken(accent));
    doc.text(initialsOf(node.name), s(cx), s(cy), { align: 'center', baseline: 'middle' });
  };

  const drawShadow = (x, y) => {
    doc.setFillColor(...COLORS.shadow);
    doc.roundedRect(s(x + 1.5), s(y + 2.5), s(CARD_W), s(CARD_H), s(8), s(8), 'F');
  };

  const drawCompanyCard = (node, x, y) => {
    drawShadow(x, y);
    doc.setFillColor(...COLORS.company);
    doc.roundedRect(s(x), s(y), s(CARD_W), s(CARD_H), s(8), s(8), 'F');
    doc.setFillColor(...COLORS.companyAccent);
    doc.rect(s(x + 24), s(y + CARD_H - 3), s(CARD_W - 48), s(3), 'F');

    doc.setTextColor(...COLORS.companyText);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(s(12.5));
    doc.text(fitText(node.name, CARD_W - 24), s(x + CARD_W / 2), s(y + CARD_H / 2 - 3), { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(s(8.5));
    doc.setTextColor(...COLORS.companySubtext);
    doc.text(`${employeeCount} ${employeeCount === 1 ? 'employee' : 'employees'}`, s(x + CARD_W / 2), s(y + CARD_H / 2 + 13), { align: 'center' });
  };

  const drawCard = (node) => {
    const x = ox + node.x;
    const y = oy + node.y;

    if (node.is_company) {
      drawCompanyCard(node, x, y);
      return;
    }

    const colors = colorsOf(node);
    const [accent] = colors;

    // Card body with a department-coloured stripe down the left edge.
    drawShadow(x, y);
    doc.setFillColor(...accent);
    doc.roundedRect(s(x), s(y), s(CARD_W), s(CARD_H), s(8), s(8), 'F');
    doc.setFillColor(...COLORS.card);
    doc.rect(s(x + ACCENT_W), s(y), s(8), s(CARD_H), 'F');
    doc.roundedRect(s(x + ACCENT_W), s(y), s(CARD_W - ACCENT_W), s(CARD_H), s(8), s(8), 'F');
    doc.setDrawColor(...(node.is_hod ? COLORS.hod : COLORS.border));
    doc.setLineWidth(s(node.is_hod ? 1.25 : 0.75));
    doc.roundedRect(s(x), s(y), s(CARD_W), s(CARD_H), s(8), s(8), 'S');

    drawAvatar(node, x + ACCENT_W + 12 + AVATAR_R, y + CARD_H / 2, colors);

    const textColors = { name: COLORS.name, designation: COLORS.muted, department: darken(accent) };
    const textH = Object.entries(TEXT_STYLES).reduce((sum, [key, style]) => sum + node.lines[key].length * style.lineH, 0);
    let baseline = y + (CARD_H - textH) / 2 + 8.5;

    Object.entries(TEXT_STYLES).forEach(([key, style]) => {
      doc.setFont('helvetica', style.style);
      doc.setFontSize(s(style.size));
      doc.setTextColor(...textColors[key]);
      node.lines[key].forEach((textLine) => {
        doc.text(textLine, s(x + TEXT_X), s(baseline));
        baseline += style.lineH;
      });
    });

    // Pills sit on the card's edges, clear of the text.
    if (node.is_hod) {
      pill(x + CARD_W - 32, y, 'HOD', COLORS.hod, COLORS.hodText);
    }
    const reports = node.children?.length || 0;
    if (reports) {
      pill(x + CARD_W - 46, y + CARD_H, `${reports} ${reports === 1 ? 'report' : 'reports'}`, COLORS.pill, COLORS.pillText, COLORS.border);
    }
  };

  const drawCards = (node) => {
    drawCard(node);
    (node.children || []).forEach(drawCards);
  };

  drawConnectors(root);
  drawCards(root);

  return doc;
};

export const downloadOrganogramPdf = ({ companyName, root }) => {
  const exportedAt = new Date();
  const doc = buildOrganogramPdf({ companyName, root, exportedAt });
  doc.save(organogramFileName(companyName, exportedAt));
};
